/**
 * /templates — the template library, the ONE place templates are managed.
 *
 * Reached from the My Docs location strip (a route-change tab)
 * and the sidebar. `private` = my personal templates, `public` = templates
 * everyone of the install can use; editing follows the normal owner/editor
 * rule. Template managers (`templates:manage`; the server says so with
 * `can_manage`, the screen never reads the scope) see the "Everything" scope
 * and may delete or publish any template; a refused "Everything"
 * (`templates_manage_required`) falls back to the visible scope. Templates are
 * named by uuid (`template_uuid`, `owner_uuid`).
 *
 * Every action goes through /embed/v1/templates — the same API the
 * "+ New → From template…" picker and the MCP tools use.
 */

import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Copy, FileText, Folder, Languages, LayoutGrid, List, Loader2, MoreVertical, Pencil, PenTool, Plus,
  Search as SearchIcon, Share2, Trash2, Type, Upload,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Context } from '@/shared/contexts/GlobalContext.jsx';
import { currentUserUuid } from '@/shared/auth/me.js';
import { useDeleteRequest, useLoad, usePatchRequest, usePostRequest } from '@/shared/hooks/requests.js';
import { apiErrorMessage } from '@/shared/utils/apiError.js';
import { MAX_FILENAME_LENGTH, filenameProblem, nameProblemMessage } from '@/shared/utils/filenames.js';
import { FieldError } from '@/shared/ui/FieldError.jsx';
import {
  TEMPLATE, TEMPLATES, TEMPLATE_BLANK, TEMPLATE_DUPLICATE, TEMPLATE_INSTANTIATE,
} from '@/shared/utils/urls.js';
import { DocumentEditorModal } from '@/shared/ui/DocumentEditorModal.jsx';
import { TruncatedTooltip } from '@/shared/ui/TruncatedTooltip.jsx';
import { VisibilityControl } from '@/shared/ui/VisibilityControl.jsx';
import { SharingModal } from '@/modules/shared/components/SharingModal.jsx';
import { LocationTabs } from '@/modules/my-docs/components/listing/LocationTabs.jsx';
import {
  TEMPLATE_LANGUAGE_LABELS, TEMPLATE_LANGUAGE_SHORT, displayName, languageForUi, orderedVariants, resolveVariant,
} from '@/shared/utils/languages.js';
import { AddLanguageDialog } from '../components/AddLanguageDialog.jsx';
import { FillFromRecordDialog } from '../components/FillFromRecordDialog.jsx';
import { UploadTemplatesDialog } from '../components/UploadTemplatesDialog.jsx';
import { TemplateBadge } from '@/modules/my-docs/components/listing/TemplateBadge.jsx';
import { VisibilityBadge } from '@/modules/my-docs/components/listing/VisibilityBadge.jsx';

const SCOPES = ['visible', 'mine', 'shared'];

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'dd.MM.yyyy HH:mm');
};

/** The server's `scope` for a pill — "shared" is `visible` minus mine, done client-side. */
const serverScope = (scope) => (scope === 'shared' ? 'visible' : scope);

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[4px] px-2.5 py-1 text-xs font-medium cursor-pointer ${
        active ? 'bg-brand text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

/** Language chips of a family; one chip per member, the resolved one lit. */
function LanguageChips({ tpl, uiLang, onPick, className = '' }) {
  const variants = orderedVariants(tpl);
  if (variants.length < 2) return null;
  const active = resolveVariant(tpl, uiLang)?.uuid;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} onClick={(e) => e.stopPropagation()}>
      {variants.map((v) => (
        <button
          key={v.uuid}
          type="button"
          onClick={() => onPick(v)}
          title={`${TEMPLATE_LANGUAGE_LABELS[v.language] || v.language || '?'}: ${v.name || ''}`}
          aria-label={`${TEMPLATE_LANGUAGE_LABELS[v.language] || v.language || '?'}`}
          data-language={v.language || ''}
          className={`rounded-[3px] px-1.5 py-0.5 text-[10px] font-semibold leading-none cursor-pointer ${
            v.uuid === active ? 'bg-brand text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
          }`}
        >
          {TEMPLATE_LANGUAGE_SHORT[v.language] || (v.language || '?').toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function TemplateActions({ tpl, mine, canManage, onUse, onEdit, onDuplicate, onShare, onRename, onDelete, onAddLanguage }) {
  const { t } = useTranslation(['my-docs']);
  const variants = orderedVariants(tpl);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
          aria-label={t('my-docs:templates.actions')}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => onUse(tpl)} className="gap-2 cursor-pointer">
          <FileText className="h-4 w-4" /> {t('my-docs:templates.use')}
        </DropdownMenuItem>
        {variants.length > 1 && variants.map((v) => (
          <DropdownMenuItem key={v.uuid} onClick={() => onUse(tpl, v.language)} className="gap-2 pl-8 cursor-pointer" data-use-in={v.language || ''}>
            {t('my-docs:templates.useIn')} {TEMPLATE_LANGUAGE_LABELS[v.language] || v.language}
          </DropdownMenuItem>
        ))}
        {tpl.canEdit && (
          <DropdownMenuItem onClick={() => onEdit(tpl)} className="gap-2 cursor-pointer">
            <Pencil className="h-4 w-4" /> {t('my-docs:templates.edit')}
          </DropdownMenuItem>
        )}
        {(mine || canManage) && (
          <DropdownMenuItem onClick={() => onAddLanguage(tpl)} className="gap-2 cursor-pointer">
            <Languages className="h-4 w-4" /> {t('my-docs:templates.addLanguage')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => onDuplicate(tpl)} className="gap-2 cursor-pointer">
          <Copy className="h-4 w-4" /> {t('my-docs:templates.duplicate')}
        </DropdownMenuItem>
        {mine && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onRename(tpl)} className="gap-2 cursor-pointer">
              <Type className="h-4 w-4" /> {t('my-docs:templates.rename')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onShare(tpl)} className="gap-2 cursor-pointer">
              <Share2 className="h-4 w-4" /> {t('my-docs:templates.share')}
            </DropdownMenuItem>
          </>
        )}
        {(mine || canManage) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(tpl)} className="gap-2 text-red-600 focus:text-red-600 cursor-pointer">
              <Trash2 className="h-4 w-4" /> {t('my-docs:templates.delete')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TemplateLibraryPage() {
  const { t, i18n } = useTranslation(['my-docs', 'mainPage', 'assistant']);
  const navigate = useNavigate();
  const { viewMode, setViewMode } = useContext(Context);
  const me = currentUserUuid();
  const isMine = (tpl) => me != null && tpl?.ownerUuid === me;

  const [scope, setScope] = useState('visible');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [editing, setEditing] = useState(null); // template open in the editor (author mode)
  const [created, setCreated] = useState(null); // document just instantiated
  const [filling, setFilling] = useState(null); // {tpl, language}: a template about another module's record
  const [sharing, setSharing] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameError, setRenameError] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [addingLanguage, setAddingLanguage] = useState(null); // family getting a new language version
  const [uploading, setUploading] = useState(false); // the Upload dialog (files + who they are for)
  const [busyUuid, setBusyUuid] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useLoad(
    {
      url: TEMPLATES,
      params: { scope: serverScope(scope), size: 100, ...(debounced ? { search: debounced } : {}) },
    },
    [scope, debounced],
  );
  const reload = () => load.request();
  const canManage = Boolean(load.response?.canManage);
  // "Everything" refused (403 `templates_manage_required`: the person no
  // longer holds `templates:manage`): the toast says why, and the list goes
  // back to what they may see.
  const refused = load.error?.data?.detail?.code;
  const { setError: clearLoadError } = load;
  useEffect(() => {
    if (scope === 'all' && refused === 'templates_manage_required') {
      clearLoadError({});
      setScope('visible');
    }
  }, [scope, refused, clearLoadError]);
  const allItems = Array.isArray(load.response?.items) ? load.response.items : [];
  const items = scope === 'shared' ? allItems.filter((tpl) => !isMine(tpl)) : allItems;

  const createBlank = usePostRequest({ url: TEMPLATE_BLANK });
  const act = usePostRequest({});
  const patch = usePatchRequest({ showError: false });
  const remove = useDeleteRequest({ showError: false });

  const tabs = [
    { value: 'my_documents', label: t('mainPage:locations.myDocuments'), icon: <Folder className="w-4 h-4" />, onClick: () => navigate('/my-docs') },
    { value: 'templates', label: t('mainPage:locations.templates'), icon: <PenTool className="w-4 h-4" />, count: load.response?.total || 0, onClick: () => {} },
  ];

  const handleNew = async () => {
    const { success, response } = await createBlank.request({ data: { visibility: 'private' } });
    if (success && response?.uuid) {
      setEditing({ uuid: response.uuid, name: response.name });
      reload();
    }
  };

  // `extra`: values and the record they came from, for a template about
  // another module's record (filled on the server as it is made).
  const instantiate = async (tpl, language, extra = {}) => {
    setBusyUuid(tpl.uuid);
    const { success, response, errorInfo } = await act.request({
      url: TEMPLATE_INSTANTIATE.replace('{template_uuid}', tpl.uuid),
      data: { parentUuid: null, language, ...extra },
      showError: false,
    });
    setBusyUuid(null);
    if (!success) { toast.error(apiErrorMessage(errorInfo)); return false; }
    toast.success(t('my-docs:templates.docCreated'));
    setCreated({ uuid: response.uuid, name: response.name });
    return true;
  };

  // `language` = an explicit chip/menu choice; otherwise the member that
  // serves the UI language (the server resolves it again and falls back).
  // A template about another module's record asks for the record first.
  const handleUse = (tpl, language) => {
    const chosen = language ?? languageForUi(tpl, i18n.language);
    if (tpl.templateEntity) { setFilling({ tpl, language: chosen }); return; }
    instantiate(tpl, chosen);
  };

  const handleFilled = async ({ values, sourceRef }) => {
    const extra = {
      ...(values.length ? { values } : {}),
      ...(sourceRef ? { sourceRef } : {}),
    };
    if (await instantiate(filling.tpl, filling.language, extra)) setFilling(null);
  };

  const handleDuplicate = async (tpl) => {
    setBusyUuid(tpl.uuid);
    const { success, errorInfo } = await act.request({ url: TEMPLATE_DUPLICATE.replace('{template_uuid}', tpl.uuid), showError: false });
    setBusyUuid(null);
    if (!success) { toast.error(apiErrorMessage(errorInfo)); return; }
    toast.success(t('my-docs:templates.copiedToMine'));
    if (scope !== 'mine') setScope('mine'); else reload();
  };

  const handleRename = async () => {
    const name = renameValue.trim();
    if (!renaming || patch.loading) return;
    // A template is a file: the file-name rule (app/core/filenames.py).
    const problem = filenameProblem(name);
    if (problem) { setRenameError(nameProblemMessage(t, problem, 'file')); return; }
    const { success, errorInfo } = await patch.request({ url: TEMPLATE.replace('{template_uuid}', renaming.uuid), data: { name }, showError: false });
    if (!success) {
      setRenameError(errorInfo?.code === 'invalid_filename'
        ? nameProblemMessage(t, errorInfo.params?.reason || 'invalid_chars', 'file')
        : apiErrorMessage(errorInfo));
      return;
    }
    setRenaming(null);
    reload();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    const { success, errorInfo } = await remove.request({ url: TEMPLATE.replace('{template_uuid}', deleting.uuid) });
    if (!success) { toast.error(apiErrorMessage(errorInfo)); return; }
    toast.success(t('my-docs:templates.deleted'));
    setDeleting(null);
    reload();
  };

  // Edit opens the member that serves the UI language when it is editable,
  // else the first editable member (a shared editor may hold one language).
  const editable = (x) => {
    const resolved = resolveVariant(x, i18n.language);
    if (resolved?.canEdit) return resolved;
    return orderedVariants(x).find((v) => v.canEdit) || x;
  };

  const actionProps = (tpl) => ({
    tpl,
    mine: isMine(tpl),
    canManage,
    onUse: (x, language) => handleUse(x, language),
    onEdit: (x) => { const v = editable(x); setEditing({ uuid: v.uuid, name: v.name }); },
    onAddLanguage: setAddingLanguage,
    onDuplicate: handleDuplicate,
    onShare: setSharing,
    onRename: (x) => { setRenaming(x); setRenameValue(x.name || ''); setRenameError(''); },
    onDelete: setDeleting,
  });

  const visibilityControl = (tpl) => (
    isMine(tpl) || canManage ? (
      <VisibilityControl
        nodeUuid={tpl.uuid}
        visibility={tpl.visibility}
        url={TEMPLATE.replace('{template_uuid}', tpl.uuid)}
        onChanged={reload}
      />
    ) : (
      <VisibilityBadge visibility={tpl.visibility} />
    )
  );

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="mx-auto max-w-5xl px-3 py-4 md:px-6">
        <button
          type="button"
          onClick={() => navigate('/my-docs')}
          className="mb-3 -ml-2 inline-flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" /> {t('my-docs:templates.back')}
        </button>
        <div className="mb-1 flex items-center justify-between gap-2">
          <h1 className="text-lg font-semibold text-foreground">{t('my-docs:templates.library')}</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setUploading(true)}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50 cursor-pointer"
            >
              <Upload className="h-3.5 w-3.5" />
              {t('my-docs:templates.upload')}
            </button>
            <button
              type="button"
              onClick={handleNew}
              disabled={createBlank.loading}
              className="inline-flex h-8 items-center gap-1 rounded-md bg-brand px-3 text-xs font-medium text-white disabled:opacity-50 cursor-pointer"
            >
              {createBlank.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {t('my-docs:templates.newTemplate')}
            </button>
          </div>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">{t('my-docs:templates.subtitle')}</p>

        <LocationTabs tabs={tabs} activeValue="templates" className="mb-3 rounded-md bg-muted/60 p-1" />

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            {SCOPES.map((value) => (
              <Pill key={value} active={scope === value} onClick={() => setScope(value)}>
                {t(`my-docs:templates.scope.${value}`)}
              </Pill>
            ))}
            {canManage && (
              <Pill active={scope === 'all'} onClick={() => setScope('all')}>
                {t('my-docs:templates.scope.all')}
              </Pill>
            )}
          </div>
          <div className="relative ml-auto w-full sm:w-64">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('my-docs:templates.search')}
              className="h-8 w-full rounded-[4px] border border-border bg-card pl-8 pr-2 text-sm text-foreground outline-none focus:border-brand"
            />
          </div>
          <div className="flex items-center rounded-[4px] border border-border bg-muted/60 p-0.5">
            <button type="button" onClick={() => setViewMode('cards')} className={`rounded-[3px] p-1 cursor-pointer ${viewMode !== 'list' ? 'bg-card text-brand shadow-sm' : 'text-muted-foreground'}`} aria-label="cards">
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setViewMode('list')} className={`rounded-[3px] p-1 cursor-pointer ${viewMode === 'list' ? 'bg-card text-brand shadow-sm' : 'text-muted-foreground'}`} aria-label="list">
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        {canManage && scope === 'all' && (
          <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t('my-docs:templates.managerHint')}
          </p>
        )}

        {load.loading && allItems.length === 0 ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-md border border-dashed border-border py-12 text-center">
            <FileText className="mb-3 h-10 w-10 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">{t('my-docs:templates.empty')}</p>
            <p className="mt-1 max-w-sm text-xs text-muted-foreground">
              {debounced ? t('my-docs:templates.emptySearch') : t('my-docs:templates.emptyDesc')}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="overflow-hidden rounded-md border border-border bg-card">
            {items.map((tpl) => (
              <div
                key={tpl.uuid}
                data-row-id={tpl.uuid}
                className="flex items-center gap-3 border-b border-border/60 px-3 py-2.5 last:border-b-0 hover:bg-muted/40"
              >
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-brand/10 text-brand">
                  {busyUuid === tpl.uuid ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <TruncatedTooltip content={displayName(tpl, i18n.language)}>
                    <button type="button" onClick={() => handleUse(tpl)} className="block max-w-full truncate text-left text-sm font-medium text-foreground hover:text-brand cursor-pointer">
                      {displayName(tpl, i18n.language)}
                    </button>
                  </TruncatedTooltip>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <span>{fmtDate(tpl.updatedAt)}</span>
                    {isMine(tpl) && <span>· {t('my-docs:templates.mine')}</span>}
                    <LanguageChips tpl={tpl} uiLang={i18n.language} onPick={(v) => (v.canEdit ? setEditing({ uuid: v.uuid, name: v.name }) : handleUse(tpl, v.language))} />
                  </div>
                </div>
                {visibilityControl(tpl)}
                <TemplateActions {...actionProps(tpl)} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-4">
            {items.map((tpl) => (
              <div key={tpl.uuid} data-row-id={tpl.uuid} className="group flex w-[150px] flex-col">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => handleUse(tpl)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleUse(tpl); }}
                  className="relative flex h-[150px] w-[150px] cursor-pointer flex-col justify-between rounded-md border border-border bg-card p-3 shadow-sm transition-transform group-hover:scale-[1.03]"
                >
                  <div className="flex items-start justify-between">
                    {busyUuid === tpl.uuid ? <Loader2 className="h-5 w-5 animate-spin text-brand" /> : <FileText className="h-5 w-5 text-brand" />}
                    <div onClick={(e) => e.stopPropagation()}>{visibilityControl(tpl)}</div>
                  </div>
                  <div className="flex flex-col items-start gap-1">
                    <TemplateBadge fileType="template" />
                    {isMine(tpl) && (
                      <span className="text-[10px] font-medium text-muted-foreground">{t('my-docs:templates.mine')}</span>
                    )}
                    <LanguageChips tpl={tpl} uiLang={i18n.language} onPick={(v) => (v.canEdit ? setEditing({ uuid: v.uuid, name: v.name }) : handleUse(tpl, v.language))} />
                  </div>
                  {/* Visual only: with pointer events it sat on top of the lock
                      control and the language chips and swallowed their clicks;
                      the card itself already handles Use. */}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-black/5 opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="rounded bg-card px-2 py-1 text-xs font-semibold text-brand shadow-sm">{t('my-docs:templates.use')}</span>
                  </div>
                </div>
                <div className="mt-2 flex min-w-0 items-start gap-1">
                  <TruncatedTooltip content={displayName(tpl, i18n.language)} className="min-w-0 flex-1">
                    <p className="min-w-0 truncate text-sm font-medium text-foreground">{displayName(tpl, i18n.language)}</p>
                  </TruncatedTooltip>
                  <TemplateActions {...actionProps(tpl)} />
                </div>
                <p className="text-[11px] text-muted-foreground">{fmtDate(tpl.updatedAt)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <UploadTemplatesDialog open={uploading} onOpenChange={setUploading} onUploaded={reload} />

      <FillFromRecordDialog
        open={Boolean(filling)}
        template={filling?.tpl}
        language={filling?.language}
        busy={Boolean(filling) && busyUuid === filling?.tpl?.uuid}
        onCancel={() => setFilling(null)}
        onCreate={handleFilled}
      />

      <AddLanguageDialog
        family={addingLanguage}
        open={Boolean(addingLanguage)}
        onOpenChange={(o) => { if (!o) setAddingLanguage(null); }}
        onAdded={reload}
      />

      {editing && (
        <DocumentEditorModal
          open
          doc={editing}
          onClose={() => { setEditing(null); reload(); }}
          onSave={reload}
        />
      )}
      {created && (
        <DocumentEditorModal
          open
          doc={created}
          onClose={() => setCreated(null)}
        />
      )}
      {sharing && (
        <SharingModal
          openShare
          setOpenShare={(open) => { if (!open) setSharing(null); }}
          nodeUuid={sharing.uuid}
          file={sharing}
          type="file"
        />
      )}

      <Dialog open={!!renaming} onOpenChange={(open) => { if (!open) setRenaming(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('my-docs:templates.renameTitle')}</DialogTitle>
          </DialogHeader>
          <input
            autoFocus
            data-testid="template-rename-input"
            value={renameValue}
            required
            maxLength={MAX_FILENAME_LENGTH}
            aria-label={t('my-docs:templates.renameTitle')}
            aria-invalid={Boolean(renameError) || undefined}
            aria-describedby={renameError ? 'template-rename-error' : undefined}
            onChange={(e) => { setRenameValue(e.target.value); setRenameError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
            className="h-9 w-full rounded-[4px] border border-border bg-card px-2 text-sm text-foreground outline-none focus:border-brand"
          />
          <FieldError id="template-rename-error" message={renameError} />
          <DialogFooter>
            <button type="button" onClick={() => setRenaming(null)} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium cursor-pointer">
              {t('my-docs:templates.cancel')}
            </button>
            <button type="button" onClick={handleRename} disabled={patch.loading || !renameValue.trim()} data-testid="template-rename-save" className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 cursor-pointer">
              {t('my-docs:templates.save')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('my-docs:templates.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {orderedVariants(deleting).length > 1
                ? t('my-docs:templates.deleteFamilyDescription', { name: displayName(deleting, i18n.language), count: orderedVariants(deleting).length - 1 })
                : t('my-docs:templates.deleteDescription', { name: deleting?.name || '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button type="button" onClick={() => setDeleting(null)} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium cursor-pointer">
              {t('my-docs:templates.cancel')}
            </button>
            <button type="button" onClick={handleDelete} disabled={remove.loading} className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 cursor-pointer">
              {t('my-docs:templates.delete')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
